import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

/**
 * Validator that checks if the control's value matches another control's value.
 * Typically used for email confirmation fields.
 *
 * @param matchTo The name of the sibling control to match against.
 */
export function emailMatchValidator(matchTo: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const parent = control.parent;
    if (!parent) return null;

    const matchControl = parent.get(matchTo);
    if (!matchControl) return null;

    if (control.value !== matchControl.value) {
      return { emailMismatch: true };
    }
    return null;
  };
}

export function urlValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  if (!value) {
    return null; // Empty URLs are handled by Validators.required if needed
  }

  // Pattern allows:
  // - "https://adesso.de" or "http://adesso.de"
  // - "www.adesso.de"
  // - "adesso.de"
  const urlPattern = /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;

  if (!urlPattern.test(value)) {
    return { invalidUrl: true };
  }

  return null;
}

export function commercialRegisterNumberValidator(
  control: AbstractControl
): ValidationErrors | null {
  const isValid = Validators.pattern('^HR[AB][0-9]{1,6}$');
  const errors = isValid(control);

  if (errors && errors['pattern']) {
    return {
      invalidCommercialRegisterNumber: true
    };
  }
  return null;
}

export function taxIdNumberValidator(control: AbstractControl): ValidationErrors | null {
  const isValid = Validators.pattern(
    '^(AT|BE|BG|CY|CZ|DE|DK|EE|EL|ES|FI|FR|HR|HU|IE|IT|LT|LU|LV|MT|NL|PL|PT|RO|SE|SI|SK|XI)[0-9A-Z]{8,12}$'
  );
  const errors = isValid(control);

  if (errors && errors['pattern']) {
    return {
      invalidTaxationNumber: true
    };
  }
  return null;
}

/**
 * Complete date validator that ensures a date field has a fully valid date.
 * Rejects incomplete dates (e.g., null, undefined, invalid dates).
 * Only accepts valid Date objects with complete day, month, and year.
 */
export function completeDateValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  // Empty values are handled by Validators.required
  if (!value) {
    return null;
  }

  // Check if it's a Date object
  if (!(value instanceof Date)) {
    return { incompleteDate: true };
  }

  // Check if it's a valid date (not an Invalid Date)
  if (isNaN(value.getTime())) {
    return { incompleteDate: true };
  }

  return null;
}

/**
 * PDF file validator that ensures only PDF files are uploaded.
 * Validates both by MIME type and file extension.
 */
export function pdfFileValidator(control: AbstractControl): ValidationErrors | null {
  const file = control.value;

  // Empty files are handled by Validators.required
  if (!file) {
    return null;
  }

  // Check if it's a File object
  if (!(file instanceof File)) {
    return { invalidFile: true };
  }

  // Check MIME type
  const mimeType = file.type.toLowerCase();
  if (mimeType !== 'application/pdf') {
    return { invalidFileType: true };
  }

  // Also check file extension as a secondary validation
  const fileName = file.name.toLowerCase();
  if (!fileName.endsWith('.pdf')) {
    return { invalidFileType: true };
  }

  return null;
}
